import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { FilterList, Refresh, BarChart } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar } from '../../utils/showMessage';
import JalaliDatePicker from '../../components/JalaliDatePicker';

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
  },
};

const detectBrowser = (ua = '') => {
  const normalized = ua.toLowerCase();
  if (normalized.includes('edge') || normalized.includes('edg/')) return 'Edge';
  if (normalized.includes('opr/') || normalized.includes('opera')) return 'Opera';
  if (normalized.includes('chrome') && !normalized.includes('edg') && !normalized.includes('opr')) return 'Chrome';
  if (normalized.includes('safari') && !normalized.includes('chrome')) return 'Safari';
  if (normalized.includes('firefox')) return 'Firefox';
  return 'نامشخص';
};

const detectOs = (ua = '') => {
  const normalized = ua.toLowerCase();
  if (normalized.includes('windows')) return 'Windows';
  if (normalized.includes('mac os') || normalized.includes('macintosh')) return 'macOS';
  if (normalized.includes('android')) return 'Android';
  if (normalized.includes('iphone') || normalized.includes('ipad')) return 'iOS';
  if (normalized.includes('linux')) return 'Linux';
  return 'نامشخص';
};

const summarizeAgents = (agents) => {
  const osMap = {};
  const browserMap = {};

  agents.forEach((item) => {
    const ua = item._id || '';
    const os = detectOs(ua);
    const browser = detectBrowser(ua);
    osMap[os] = (osMap[os] || 0) + item.count;
    browserMap[browser] = (browserMap[browser] || 0) + item.count;
  });

  const mapToSortedArray = (map) => Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value]) => ({ label: key, value }));

  return {
    os: mapToSortedArray(osMap),
    browsers: mapToSortedArray(browserMap),
  };
};

const PAGE_TITLES = {
  '/': 'پیشخوان',
  '/login': 'ورود',
  '/complete-profile': 'تکمیل پروفایل',
  '/profile': 'پروفایل کاربر',
  '/settings': 'تنظیمات',
  '/notifications': 'اعلان‌ها',
  '/welfare-categories': 'دسته‌بندی رفاهی',
  '/retraining': 'بازآموزی',
  '/news': 'اخبار و اطلاعیه‌ها',
  '/events': 'مراسمات',
  '/board-members': 'اعضای هیئت مدیره',
  '/assistant': 'دستیار هوشمند',
  '/contact': 'تماس و نظرات',
  '/dashboard/retraining': 'مدیریت بازآموزی',
  '/dashboard/welfare-categories': 'مدیریت دسته‌بندی رفاهی',
  '/dashboard/welfare-services': 'مدیریت خدمات رفاهی',
  '/dashboard/news': 'مدیریت اخبار',
  '/dashboard/events': 'مدیریت مراسمات',
  '/dashboard/pages': 'مدیریت صفحات',
  '/dashboard/podcasts': 'مدیریت پادکست‌ها',
  '/dashboard/slider': 'مدیریت اسلایدر',
  '/dashboard/notifications': 'مدیریت اعلان‌ها',
  '/dashboard/users-management': 'مدیریت کاربران',
  '/dashboard/deputys': 'مدیریت معاونت‌ها',
  '/dashboard/contact-settings': 'تنظیمات تماس',
  '/dashboard/contact-messages': 'پیام‌های تماس',
  '/dashboard/board-members': 'مدیریت هیئت مدیره',
  '/dashboard/page-view-report': 'گزارش ورود صفحات',
  '/dashboard/audit-logs': 'لاگ‌های مدیریتی',
  '/dashboard/ratings': 'مدیریت امتیازها',
};

const PAGE_TITLE_PATTERNS = [
  { pattern: /^\/newsdetail\/[^/]+$/, title: 'جزئیات خبر' },
  { pattern: /^\/eventdetail\/[^/]+$/, title: 'جزئیات مراسم' },
  { pattern: /^\/welfare\/service\/[^/]+$/, title: 'جزئیات خدمات رفاهی' },
  { pattern: /^\/welfare\/[^/]+$/, title: 'لیست خدمات رفاهی' },
];

const resolvePageTitle = (rawPath) => {
  if (!rawPath) return '-';
  const path = rawPath.split('?')[0].replace(/\/$/, '') || '/';
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  const matched = PAGE_TITLE_PATTERNS.find((entry) => entry.pattern.test(path));
  return matched ? matched.title : '-';
};

const formatDateString = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fa-IR');
};

const initialSectionCounts = {
  services: 0,
  news: 0,
  events: 0,
  boardMembers: 0,
  assistant: 0,
  contact: 0,
};

const PageViewReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState({ from: '', to: '', resource: '' });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ pages: [], userAgents: [], resources: [] });
  const [sectionCounts, setSectionCounts] = useState(initialSectionCounts);

  const totalViews = useMemo(() => data.pages.reduce((sum, item) => sum + (item.count || 0), 0), [data.pages]);
  const uniquePages = data.pages.length;
  const summaries = useMemo(() => summarizeAgents(data.userAgents || []), [data.userAgents]);
  const resourceSummary = useMemo(() => (data.resources || []).map((entry) => ({ label: entry._id, value: entry.count })), [data.resources]);
  const sectionCards = useMemo(() => [
    { key: 'services', label: 'سرویس‌های رفاهی', description: 'خدمات' },
    { key: 'news', label: 'اخبار', description: 'خبرها' },
    { key: 'events', label: 'مراسمات', description: 'رویدادها' },
    { key: 'boardMembers', label: 'اعضای هیئت مدیره', description: 'مدیران' },
    { key: 'assistant', label: 'دستیار هوشمند', description: 'پاسخ‌گو' },
    { key: 'contact', label: 'تماس و نظرات', description: 'تماس‌ها' },
  ], [sectionCounts]);

  const loadReport = async (overrideFilters) => {
    setLoading(true);
    try {
      const params: any = {};
      const sourceFilters = overrideFilters || filters;
      if (sourceFilters.from) params.from = sourceFilters.from;
      if (sourceFilters.to) params.to = sourceFilters.to;
      if (sourceFilters.resource) params.resource = sourceFilters.resource;
      const res = await api.get('/admin/audit-logs/page-views', { params });
      setData({
        pages: res?.data?.pages || [],
        userAgents: res?.data?.userAgents || [],
        resources: res?.data?.resources || [],
      });
      setSectionCounts({
        ...initialSectionCounts,
        ...(res?.data?.sectionCounts || {}),
      });
    } catch (error) {
      console.error('page view report error', error);
      snackbar('خطا در بارگذاری گزارش ورودها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleDateChange = (name) => (iso) => {
    setFilters({ ...filters, [name]: iso });
  };

  const handleApply = () => {
    loadReport(filters);
  };

  const handleClear = () => {
    const cleared = { from: '', to: '', resource: '' };
    setFilters(cleared);
    loadReport(cleared);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <BarChart fontSize="small" />
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">گزارش ورود به صفحات</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => loadReport(filters)}
            disabled={loading}
          >
            بروزرسانی
          </Button>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" mb={2}>فیلتر بازه تاریخ</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <JalaliDatePicker
              label="از تاریخ"
              value={filters.from}
              onChange={handleDateChange('from')}
              sx={textFieldStyle}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <JalaliDatePicker
              label="تا تاریخ"
              value={filters.to}
              onChange={handleDateChange('to')}
              endOfDay
              sx={textFieldStyle}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="منبع یا سرویس"
              name="resource"
              placeholder="مثلا welfare-services"
              value={filters.resource}
              onChange={handleFilterChange}
              fullWidth
              sx={textFieldStyle}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="contained" color="primary" onClick={handleApply} sx={{ mt: { xs: 2, md: 0 }, borderRadius: 2, minHeight: 42 }}>
              اعمال فیلتر
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="text" onClick={handleClear} sx={{ mt: { xs: 2, md: 0 }, borderRadius: 2 }}>
              پاک کردن فیلتر
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ borderRadius: 3, p: 2, minHeight: 120 }} elevation={2}>
            <Typography variant="subtitle2" color="text.secondary">کل بازدیدها</Typography>
            <Typography variant="h4" fontWeight="bold">{totalViews}</Typography>
            <Typography variant="caption" color="text.secondary">{formatDateString(filters.from)} ← {formatDateString(filters.to)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ borderRadius: 3, p: 2, minHeight: 120 }} elevation={2}>
            <Typography variant="subtitle2" color="text.secondary">صفحات یونیک</Typography>
            <Typography variant="h4" fontWeight="bold">{uniquePages}</Typography>
            <Typography variant="caption" color="text.secondary">بر اساس path</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ borderRadius: 3, p: 2, minHeight: 120 }} elevation={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>بازه تاریخ جاری</Typography>
            <Typography variant="body2" fontWeight="bold">{filters.from || filters.to ? `${filters.from ? formatDateString(filters.from) : 'از ابتدا'} تا ${filters.to ? formatDateString(filters.to) : 'اکنون'}` : 'بدون محدوده'}</Typography>
            <Chip label={filters.from || filters.to ? 'فیلتر فعال است' : 'بدون فیلتر'} size="small" sx={{ mt: 1 }} />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        {sectionCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.key}>
            <Paper sx={{ borderRadius: 3, p: 2, minHeight: 110 }} elevation={2}>
              <Typography variant="subtitle2" color="text.secondary">{card.label}</Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{sectionCounts[card.key] || 0}</Typography>
              <Typography variant="caption" color="text.secondary">{card.description}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
              <Typography fontWeight="bold">تعداد ورود به صفحات</Typography>
              <Chip icon={<FilterList />} label="Action view" size="small" />
            </Box>
            {loading ? (
              <Box p={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell align="right">نام صفحه</TableCell>
                      <TableCell align="right">مسیر</TableCell>
                      <TableCell align="right">تعداد</TableCell>
                      <TableCell align="right">آخرین ورود</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.pages.map((page) => (
                      <TableRow key={page._id} hover>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {resolvePageTitle(page._id)}
                        </TableCell>
                        <TableCell align="right" sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary', direction: 'ltr' }}>
                          {page._id}
                        </TableCell>
                        <TableCell align="right">{page.count}</TableCell>
                        <TableCell align="right">{page.lastSeen ? new Date(page.lastSeen).toLocaleString('fa-IR') : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {data.pages.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">داده‌ای برای نمایش وجود ندارد</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ borderRadius: 3, p: 2 }} elevation={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>سیستم‌عامل‌ها</Typography>
                <Divider sx={{ mb: 1 }} />
                {summaries.os.map((item) => (
                  <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.label}</Typography>
                    <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                  </Box>
                ))}
                {!summaries.os.length && (
                  <Typography color="text.secondary" variant="caption">موردی ثبت نشده</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ borderRadius: 3, p: 2 }} elevation={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>مرورگرها</Typography>
                <Divider sx={{ mb: 1 }} />
                {summaries.browsers.map((item) => (
                  <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.label}</Typography>
                    <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                  </Box>
                ))}
                {!summaries.browsers.length && (
                  <Typography color="text.secondary" variant="caption">داده‌ای وجود ندارد</Typography>
                )}
              </Paper>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <Paper sx={{ borderRadius: 3, p: 2 }} elevation={2}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>بیشترین منابع</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {resourceSummary.length > 0 ? resourceSummary.slice(0, 5).map((item) => (
                    <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.label}</Typography>
                      <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                    </Box>
                  )) : (
                    <Typography color="text.secondary" variant="caption">موردی ثبت نشده</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">کل {resourceSummary.length} منبع ثبت‌شده</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PageViewReport;
